import { GRADE_STYLES, STATUS_STYLES, scoreColor } from "../lib/constants";
import { Download, FileText, FileSpreadsheet } from "lucide-react";
import { downloadExport } from "../lib/apiClient";
import { toast } from "sonner";

export function GradeBadge({ grade, size = "sm" }) {
  const s = GRADE_STYLES[grade] || GRADE_STYLES["D"];
  const pad = size === "lg" ? "px-3 py-1 text-sm" : "px-2.5 py-0.5 text-xs";
  return (
    <span className={`pill ${pad}`} style={{ background: s.bg, color: s.color, borderColor: s.border }} data-testid="grade-badge">
      {grade}
    </span>
  );
}

export function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES["Pending"];
  return (
    <span className="pill" style={{ background: s.bg, color: s.color, borderColor: s.border }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
      {status}
    </span>
  );
}

export function ScoreBar({ value, max = 100, showValue = true }) {
  const pct = Math.min(100, (value / max) * 100);
  const color = scoreColor((value / max) * 100);
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="progress-track flex-1">
        <div className="progress-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      {showValue && <span className="mono text-xs font-semibold" style={{ minWidth: 38 }}>{value?.toFixed ? value.toFixed(1) : value}</span>}
    </div>
  );
}

export function PageHeader({ title, subtitle, exportModule, exportParams, children }) {
  const doExport = (format) => {
    if (!exportModule) return;
    downloadExport(exportModule, format, exportParams || {});
    toast.success(`${format === "pdf" ? "PDF" : "Excel"} export started`, { description: "Your report download will begin shortly." });
  };
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">{title}</h1>
        {subtitle && <p className="text-sm text-secondary mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">
        {children}
        {exportModule && (
          <>
            <button data-testid="export-pdf-button" onClick={() => doExport("pdf")}
              className="pill px-3 py-2 text-sm font-semibold surface border-strong hover:brand-text transition-colors">
              <FileText size={15} /> PDF
            </button>
            <button data-testid="export-excel-button" onClick={() => doExport("excel")}
              className="pill px-3 py-2 text-sm font-semibold surface border-strong hover:brand-text transition-colors">
              <FileSpreadsheet size={15} /> Excel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function StatCard({ label, value, sub, icon: Icon, accent = "#2563EB", testid }) {
  return (
    <div className="card-panel p-5 fade-up" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-c">{label}</p>
          <p className="kpi-value text-2xl sm:text-3xl mt-2" style={{ color: "var(--text-primary)" }}>{value}</p>
          {sub && <p className="text-xs text-secondary mt-1.5">{sub}</p>}
        </div>
        {Icon && (
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
            <Icon size={19} />
          </div>
        )}
      </div>
    </div>
  );
}

export function Empty({ text = "No data available" }) {
  return <div className="py-12 text-center text-sm text-muted-c">{text}</div>;
}

export function Avatar({ src, name, size = 36 }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="rounded-full flex items-center justify-center mono text-xs font-bold shrink-0" style={{ width: size, height: size, background: "var(--brand-glow)", color: "var(--brand)" }}>{initials}</div>
  );
}
